import { useState, useEffect } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { handleYouTubeCallback, fetchYouTubeChannels, confirmYouTubeChannel } from '@/lib/api'
import { SimpleIcon } from '@adthlete/common'
import { Check, X, Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

const STEP_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  SUCCESS: 'success',
  ERROR: 'error',
}

interface Channel {
  id: string
  snippet?: {
    title?: string
    description?: string
    thumbnails?: {
      default?: {
        url?: string
      }
      medium?: {
        url?: string
      }
    }
  }
  statistics?: {
    subscriberCount?: string
    videoCount?: string
    viewCount?: string
  }
}

interface ConnectionInfo {
  channel_title?: string
  subscriber_count?: number
  video_count?: number
  view_count?: number
  thumbnail?: string
}

function YouTubeAuthSteps() {
  const location = useLocation()
  const navigate = useNavigate()
  const { refreshUser } = useAuth()

  const [steps, setSteps] = useState([
    {
      id: 1,
      title: 'Visit YouTube and provide authorization',
      description: 'Redirecting you to Google to grant YouTube permissions',
      status: STEP_STATUS.PENDING,
      error: null,
    },
    {
      id: 2,
      title: 'Verifying your YouTube account using the authorization code',
      description: 'Processing the authorization code from Google',
      status: STEP_STATUS.PENDING,
      error: null,
    },
    {
      id: 3,
      title: 'Checking for channels associated with your YouTube account',
      description: 'Finding YouTube channels linked to your account',
      status: STEP_STATUS.PENDING,
      error: null,
    },
    {
      id: 4,
      title: 'Select the channel you want to connect',
      description: 'Choose which YouTube channel to connect to your profile',
      status: STEP_STATUS.PENDING,
      error: null,
    },
  ])

  const [currentStep, setCurrentStep] = useState(0)
  const [availableChannels, setAvailableChannels] = useState<Channel[]>([])
  const [tokenId, setTokenId] = useState<string | null>(null)
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null)
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo | null>(null)
  const [isComplete, setIsComplete] = useState(false)

  useEffect(() => {
    // Don't run the callback logic if we're already complete
    if (isComplete) {
      return
    }

    const queryParams = new URLSearchParams(location.search)
    const code = queryParams.get('code')
    const state = queryParams.get('state')
    const errorParam = queryParams.get('error')
    const errorDescription = queryParams.get('error_description')

    if (code) {
      // We're coming back from Google with an authorization code
      handleYouTubeCallbackData(code, state)
    } else if (errorParam) {
      // There was an error during YouTube authorization
      updateStepStatus(1, STEP_STATUS.ERROR, errorDescription || 'YouTube authorization failed')
    } else {
      // No code means we need to start the YouTube authorization process
      startYouTubeAuth()
    }
  }, [location.search, isComplete])

  const updateStepStatus = (stepId: number, status: string, error: string | null = null) => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => (step.id === stepId ? { ...step, status, error } : step)),
    )
  }

  const startYouTubeAuth = () => {
    // Step 1: Redirect to Google (this should already be complete if we're here)
    updateStepStatus(1, STEP_STATUS.SUCCESS)
    setCurrentStep(1)
  }

  const handleYouTubeCallbackData = async (code: string, state: string | null) => {
    try {
      // Step 1 is complete (we got the code)
      updateStepStatus(1, STEP_STATUS.SUCCESS)

      // Step 2: Process the authorization code and verify token
      updateStepStatus(2, STEP_STATUS.IN_PROGRESS)
      setCurrentStep(2)

      const pending_social_id = localStorage.getItem('pending_social_id')

      const response = await handleYouTubeCallback({
        code: code,
        state: state || undefined,
        social_account_id: pending_social_id || undefined,
      })

      if (response.error) {
        updateStepStatus(2, STEP_STATUS.ERROR, response.error)
        return
      }

      updateStepStatus(2, STEP_STATUS.SUCCESS)

      // Step 3: Fetch YouTube channels
      updateStepStatus(3, STEP_STATUS.IN_PROGRESS)
      setCurrentStep(3)

      await fetchYouTubeChannelsData()
    } catch (error: any) {
      updateStepStatus(
        2,
        STEP_STATUS.ERROR,
        error.message || 'Failed to process authorization code',
      )
    }
  }

  const fetchYouTubeChannelsData = async () => {
    try {
      const pending_social_id = localStorage.getItem('pending_social_id')
      const response = await fetchYouTubeChannels(pending_social_id || undefined)

      if (response.error) {
        updateStepStatus(
          3,
          STEP_STATUS.ERROR,
          'No YouTube channels found or error fetching channels',
        )
        return
      }

      const { channels, token_id } = response
      setAvailableChannels(channels || [])
      setTokenId(token_id)

      if (!channels || channels.length === 0) {
        updateStepStatus(3, STEP_STATUS.ERROR, 'No YouTube channels associated with your account')
        return
      }

      updateStepStatus(3, STEP_STATUS.SUCCESS)
      setCurrentStep(4)
    } catch (error: any) {
      updateStepStatus(3, STEP_STATUS.ERROR, error.message || 'Failed to fetch YouTube channels')
    }
  }

  const handleChannelSelection = async (channel: Channel) => {
    try {
      updateStepStatus(4, STEP_STATUS.IN_PROGRESS)
      setSelectedChannel(channel)

      const pending_social_id = localStorage.getItem('pending_social_id')

      if (!tokenId) {
        throw new Error('No token ID available')
      }

      await confirmYouTubeChannel({
        channel_id: channel.id,
        social_account_id: pending_social_id || undefined,
        token_id: tokenId,
      })

      // Set success state immediately after API success
      updateStepStatus(4, STEP_STATUS.SUCCESS)
      setConnectionInfo({
        channel_title: channel.snippet?.title,
        subscriber_count: parseInt(channel.statistics?.subscriberCount || '0'),
        video_count: parseInt(channel.statistics?.videoCount || '0'),
        view_count: parseInt(channel.statistics?.viewCount || '0'),
        thumbnail: channel.snippet?.thumbnails?.default?.url,
      })
      setIsComplete(true)

      // Clear the pending social ID
      localStorage.removeItem('pending_social_id')

      // Refresh user data and redirect after showing success
      setTimeout(async () => {
        await refreshUser()
        navigate('/settings?tab=social')
      }, 2000)
    } catch (error: any) {
      updateStepStatus(4, STEP_STATUS.ERROR, error.message || 'Failed to connect YouTube channel')
    }
  }

  const getStepIcon = (step: any) => {
    switch (step.status) {
      case STEP_STATUS.SUCCESS:
        return <Check className="text-green-500 h-6 w-6" />
      case STEP_STATUS.ERROR:
        return <X className="text-red-500 h-6 w-6" />
      case STEP_STATUS.IN_PROGRESS:
        return <Loader2 className="text-blue-500 h-6 w-6 animate-spin" />
      default:
        return (
          <div className="w-6 h-6 rounded-full bg-gray-500 flex items-center justify-center text-white text-sm font-bold">
            {step.id}
          </div>
        )
    }
  }

  const getStepClass = (step: any) => {
    switch (step.status) {
      case STEP_STATUS.SUCCESS:
        return 'border-green-500 bg-green-500/10'
      case STEP_STATUS.ERROR:
        return 'border-red-500 bg-red-500/10'
      case STEP_STATUS.IN_PROGRESS:
        return 'border-blue-500 bg-blue-500/10'
      default:
        return 'border-white/20 bg-white/5'
    }
  }

  const formatNumber = (num?: number): string => {
    if (!num) return '0'
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M'
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
  }

  return (
    <div className="w-full max-w-4xl mx-auto pb-4">
      <div className="mb-4 md:mb-6 text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-red-600 p-4 rounded-full">
            <SimpleIcon slug="youtube" color="white" height={32} width={32} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-white">YouTube Authorization Process</h1>
        <p className="text-gray-400">Connecting your YouTube channel...</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-6">
        <div className="space-y-4">
          {steps.map((step) => (
            <div key={step.id} className={`border rounded-lg p-4 ${getStepClass(step)}`}>
              <div className="flex items-center gap-4">
                <div className="shrink-0">{getStepIcon(step)}</div>
                <div className="grow">
                  <h3 className="text-white font-medium">{step.title}</h3>
                  <p className="text-gray-400 text-sm">{step.description}</p>
                  {step.error && <div className="text-red-400 mt-2 text-sm">{step.error}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Channel Selection UI */}
      {currentStep === 4 && availableChannels.length > 0 && !isComplete && (
        <div className="mt-4">
          <h3 className="text-white font-medium mb-3">Select your YouTube Channel</h3>
          <p className="text-gray-400 mb-4">
            Choose the YouTube channel you want to connect to your profile:
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {availableChannels.map((channel, index) => (
              <Card key={index} variant="dark">
                <CardContent className="p-4 text-center">
                  {channel.snippet?.thumbnails?.default?.url && (
                    <img
                      src={channel.snippet.thumbnails.default.url}
                      alt={channel.snippet?.title}
                      className="rounded-full mb-3 mx-auto"
                      style={{ width: '60px', height: '60px' }}
                    />
                  )}
                  <h4 className="text-white font-medium mb-2">{channel.snippet?.title}</h4>
                  <div className="text-gray-400 text-sm mb-3 space-y-1">
                    <div>
                      {formatNumber(parseInt(channel.statistics?.subscriberCount || '0'))}{' '}
                      subscribers
                    </div>
                    <div>
                      {formatNumber(parseInt(channel.statistics?.videoCount || '0'))} videos
                    </div>
                    <div>
                      {formatNumber(parseInt(channel.statistics?.viewCount || '0'))} total views
                    </div>
                  </div>
                  <Button
                    onClick={() => handleChannelSelection(channel)}
                    disabled={selectedChannel?.id === channel.id}
                    variant="lime"
                    className="w-full"
                    size="sm"
                  >
                    {selectedChannel?.id === channel.id ? 'Connecting...' : 'Select Channel'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Success State */}
      {isComplete && connectionInfo && (
        <div className="text-center">
          <Card className="bg-green-500/10 border border-green-500">
            <CardContent className="p-6">
              <div className="flex justify-center mb-4">
                <Check className="text-green-500 h-12 w-12" />
              </div>
              <h2 className="text-xl font-bold text-white mb-4">Successfully Connected!</h2>
              <div className="mb-4">
                {connectionInfo.thumbnail && (
                  <img
                    src={connectionInfo.thumbnail}
                    alt="YouTube Channel"
                    className="rounded-full mb-2 mx-auto"
                    style={{ width: '80px', height: '80px' }}
                  />
                )}
                <h3 className="text-white font-medium">{connectionInfo.channel_title}</h3>
                <div className="text-gray-400 mt-2 space-y-1">
                  <p>Subscribers: {formatNumber(connectionInfo.subscriber_count)}</p>
                  <p>Videos: {formatNumber(connectionInfo.video_count)}</p>
                  <p>Total Views: {formatNumber(connectionInfo.view_count)}</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                Your YouTube channel has been successfully connected!
              </p>
              <Link
                to="/settings?tab=social"
                className="inline-block px-6 py-2 bg-[#C0FD72] text-black rounded-lg font-medium hover:bg-[#d2ff9c] transition-colors"
              >
                Return to Settings
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error Actions */}
      {steps.some((step) => step.status === STEP_STATUS.ERROR) && !isComplete && (
        <div className="text-center">
          <Card className="bg-yellow-500/10 border border-yellow-500">
            <CardContent className="p-6">
              <h3 className="text-white font-medium mb-2">Need Help?</h3>
              <p className="text-gray-400 mb-4">
                If you're experiencing issues, please check that:
              </p>
              <ul className="text-left text-gray-400 text-sm mb-4 space-y-1">
                <li>• You have a YouTube channel associated with your Google account</li>
                <li>• Your YouTube channel has uploaded videos or content</li>
                <li>• You granted the necessary permissions during authorization</li>
              </ul>
              <div className="space-x-4">
                <Link
                  to="/connect/youtube"
                  className="inline-block px-4 py-2 bg-[#C0FD72] text-black rounded-lg font-medium hover:bg-[#d2ff9c] transition-colors"
                >
                  Try Again
                </Link>
                <a
                  href="mailto:support@adthlete.com"
                  className="inline-block px-4 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                >
                  Contact Support
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export default YouTubeAuthSteps
